import { GoogleSignInResult, cfaSignIn } from 'capacitor-firebase-auth/alternative';
import { FacebookSignInResult } from 'capacitor-firebase-auth/dist/esm/definitions';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { Capacitor } from '@capacitor/core';
import { ISuccessCallbackParams } from '@bridebook/web/lib/auth/auth-context';
import { AuthProviders } from '@bridebook/web/lib/auth/types';
import { MOBILE_APP_ID } from '@bridebook/web/lib/mobile-app/constants';
import { UrlHelper } from '@bridebook/web/lib/url-helper';

const nativeLogin = async ({
  providerId,
  successCallback,
  errorCallback,
}: {
  providerId: string;
  successCallback: ({ token }: ISuccessCallbackParams) => void;
  errorCallback: (error: any) => void;
}) => {
  const errorCallbackWithLogging = (error: any) => {
    SentryMinimal().captureException(error, {
      tags: {
        source: 'nativeLogin',
      },
    });
    errorCallback(error);
  };

  if (providerId === AuthProviders.GOOGLE) {
    cfaSignIn(providerId).subscribe({
      next: ({ result }) => {
        successCallback({ token: (result as GoogleSignInResult).idToken });
      },
      error: errorCallbackWithLogging,
    });
    return;
  }

  if (providerId === AuthProviders.FACEBOOK) {
    const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
    try {
      if (platform === 'ios') {
        const response = await FacebookLogin.login({
          permissions: ['email', 'public_profile'],
        });

        if (!response?.accessToken?.token) {
          throw new Error('Facebook login failed or returned no access token');
        }

        successCallback({ token: response.accessToken.token });
      } else {
        // Android → use cfaSignIn
        cfaSignIn(providerId).subscribe({
          next: ({ result }) =>
            successCallback({ token: (result as FacebookSignInResult).idToken }),
          error: errorCallbackWithLogging,
        });
      }
    } catch (error) {
      errorCallbackWithLogging(error);
    }
    return;
  }

  if (providerId === AuthProviders.APPLE) {
    try {
      const result = await SignInWithApple.authorize({
        clientId: MOBILE_APP_ID,
        redirectURI: UrlHelper.index,
        scopes: 'email, name',
      });
      if (!result?.response?.identityToken) {
        throw new Error('Apple sign in authorization failed. Please contact support');
      }
      successCallback({ token: result.response.identityToken });
    } catch (error) {
      errorCallbackWithLogging(error);
    }
  }
};

export default nativeLogin;
